import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, GraduationCap, Briefcase, Star } from 'lucide-react';
import { countriesAPI, testimonialsAPI, servicesAPI, articlesAPI } from '@/lib/api';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import Testimonials from '@/components/Testimonials';
import Gallery from '@/components/Gallery';
import Events from '@/components/Events';
import PartnersLogos from '@/components/PartnersLogos';
import ConsultationModal from '@/components/ConsultationModal';
import 'swiper/css';
import 'swiper/css/pagination';

// Swiper configuration constants (extracted to avoid re-creating on each render)
const SWIPER_MODULES = [Autoplay, Pagination];
const SWIPER_AUTOPLAY_CONFIG = { delay: 5000 };
const SWIPER_PAGINATION_CONFIG = { clickable: true };

const HomePage = () => {
  const [countries, setCountries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countriesRes, testimonialsRes, servicesRes, articlesRes] = await Promise.all([
          countriesAPI.getAll(),
          testimonialsAPI.getAll(),
          servicesAPI.getAll(),
          articlesAPI.getAll(),
        ]);
        setCountries(countriesRes.data.slice(0, 8));
        setTestimonials(testimonialsRes.data);
        setServices(servicesRes.data);
        setArticles(articlesRes.data.slice(0, 3));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch homepage data:', error);
        }
        // Non-blocking error - continue with empty data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative h-[600px] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1530545124313-ce5e8eae55af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhaXJwbGFuZSUyMGZseWluZyUyMGNsb3VkcyUyMHNreSUyMHRyYXZlbHxlbnwwfHx8fDE3ODM5OTAzNjN8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white mb-6" data-testid="hero-title">
            مسیر آینده شما از اینجا آغاز می‌شود
          </h1>
          <p className="text-lg text-zinc-300 mb-4 leading-relaxed">
            اطلاعات کامل کشورهای جهان، دانشگاه‌ها، فرصت‌های تحصیلی و موقعیت‌های شغلی را در یک پلتفرم پیدا کنید
          </p>
          <div className="mb-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="https://www.icef.com/agency/0016M00002HZ5bKQAT/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-transform hover:scale-105"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_career-destination/artifacts/qlrdglvs_icef.png" 
                alt="ICEF Logo" 
                className="h-16 sm:h-20 w-auto drop-shadow-2xl cursor-pointer"
                style={{ filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))' }}
              />
            </a>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/40 rounded-full backdrop-blur-sm">
              <span className="text-[#D4AF37] font-medium">✓</span>
              <span className="text-white text-sm">جزو برترین نماینده‌های شرکت آی‌سف (ICEF)</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/countries"
              data-testid="hero-countries-btn"
              className="px-8 py-4 bg-[#D4AF37] text-black rounded-full font-medium hover:bg-[#F3E5AB] transition-colors inline-flex items-center justify-center gap-2"
            >
              مشاهده کشورها
              <ArrowRight size={20} className="rotate-180" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-consultation-btn"
              className="px-8 py-4 border-2 border-[#D4AF37] text-[#D4AF37] rounded-full font-medium hover:bg-[#D4AF37] hover:text-black transition-colors"
            >
              دریافت مشاوره
            </Link>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="countries-section">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">کشور موردنظر خود را انتخاب کنید</h2>
          <p className="text-zinc-400">بهترین مقاصد مهاجرتی جهان را بررسی کنید</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {countries.map((country) => (
            <Link
              key={country.id}
              to={`/countries/${country.id}`}
              data-testid={`country-card-${country.id}`}
              className="group relative overflow-hidden rounded-2xl aspect-[3/4] glass-effect border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1 gold-glow-hover"
            >
              <img
                src={country.image_url}
                alt={country.name_fa}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-4xl mb-2">{country.flag_emoji}</div>
                <h3 className="text-2xl font-medium text-white mb-1">{country.name_fa}</h3>
                <p className="text-sm text-zinc-300">{country.capital_fa}</p>
                <div className="flex items-center gap-2 mt-3">
                  <Star size={16} className="text-[#D4AF37]" fill="#D4AF37" />
                  <span className="text-sm text-[#D4AF37]">{country.quality_of_life_score}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/countries"
            data-testid="view-all-countries"
            className="inline-flex items-center gap-2 px-6 py-3 border border-[#D4AF37] text-[#D4AF37] rounded-full hover:bg-[#D4AF37] hover:text-black transition-colors"
          >
            مشاهده همه کشورها
            <ArrowRight size={20} className="rotate-180" />
          </Link>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-[#0A0A0A] py-20" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">خدمات مهاجرتی ما</h2>
            <p className="text-zinc-400">راهنمایی کامل در تمام مراحل مهاجرت</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              // Map service IDs to actual form routes
              const serviceRoutes = {
                's1': '/services/education-consulting',  // مشاوره مهاجرت تحصیلی
                's2': '/services/education-consulting',  // اخذ پذیرش دانشگاه
                's3': '/services/visa',                  // ویزای تحصیلی
                's4': '/services/visa',                  // ویزای کاری
                's5': '/services/hotel',                 // رزرو هتل و پرواز
                's6': '/services/resume'                 // نگارش رزومه
              };
              
              const servicePath = serviceRoutes[service.id] || '/services';
              
              return (
                <Link
                  key={service.id}
                  to={servicePath}
                  data-testid={`service-card-${service.id}`}
                  className="glass-effect border border-white/10 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition-all gold-glow-hover cursor-pointer block"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                    <GraduationCap size={28} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-3">{service.title_fa}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{service.description_fa}</p>
                </Link>
              );
            })}
          </div>
          
          {/* Consultation Button */}
          <div className="text-center mt-12">
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black font-medium rounded-full hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all"
            >
              درخواست مشاوره رایگان
              <ArrowRight size={20} className="rotate-180" />
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20" data-testid="testimonials-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">نظرات مشتریان</h2>
              <p className="text-zinc-400">تجربه موفق همراهان ما</p>
            </div>
            <Swiper
              modules={SWIPER_MODULES}
              spaceBetween={30}
              slidesPerView={1}
              autoplay={SWIPER_AUTOPLAY_CONFIG}
              pagination={SWIPER_PAGINATION_CONFIG}
              className="testimonials-swiper"
            >
              {testimonials.map((testimonial) => (
                <SwiperSlide key={testimonial.id}>
                  <div className="glass-effect border border-white/10 rounded-2xl p-8 text-center">
                    <div className="flex justify-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={`star-${testimonial.id}-${i}`}
                          size={20}
                          className={i < Math.floor(testimonial.rating) ? 'text-[#D4AF37]' : 'text-zinc-600'}
                          fill={i < Math.floor(testimonial.rating) ? '#D4AF37' : 'none'}
                        />
                      ))}
                    </div>
                    <p className="text-zinc-300 leading-relaxed mb-6">"{testimonial.comment_fa}"</p>
                    <h4 className="text-lg font-medium text-white">{testimonial.name_fa}</h4>
                    <p className="text-sm text-zinc-400">{testimonial.service_type} - {testimonial.destination_country}</p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}

      {/* Articles Section */}
      {articles.length > 0 && (
        <section className="bg-[#0A0A0A] py-20" data-testid="articles-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">مقالات و راهنماها</h2>
              <p className="text-zinc-400">آخرین اطلاعات مهاجرتی</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/articles/${article.id}`}
                  data-testid={`article-card-${article.id}`}
                  className="group glass-effect border border-white/10 rounded-2xl overflow-hidden hover:border-[#D4AF37]/30 transition-all gold-glow-hover"
                >
                  <img
                    src={article.image_url}
                    alt={article.title_fa}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="p-6">
                    <span className="text-xs text-[#D4AF37] font-medium">{article.category}</span>
                    <h3 className="text-lg font-medium text-white mt-2 mb-2 line-clamp-2">{article.title_fa}</h3>
                    <p className="text-sm text-zinc-400 line-clamp-2">{article.excerpt_fa}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="glass-effect border border-[#D4AF37]/20 rounded-3xl p-12">
            <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">
              آماده برای شروع سفر مهاجرت هستید؟
            </h2>
            <p className="text-zinc-400 mb-8 leading-relaxed">
              با مشاوران حرفه‌ای ما تماس بگیرید و اولین قدم را برای آینده‌ای بهتر بردارید
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black rounded-full font-medium hover:bg-[#F3E5AB] transition-colors"
            >
              دریافت مشاوره رایگان
              <ArrowRight size={20} className="rotate-180" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <Gallery />

      {/* Events Section */}
      <Events />

      {/* Testimonials Section */}
      <Testimonials />

      {/* Partners Logos Section */}
      <PartnersLogos />

      {/* Consultation Modal */}
      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default HomePage;
